import React, { useCallback, useMemo } from 'react';
import { GridRowIdGetter, GridRowModel } from "@mui/x-data-grid-pro";
import DataGridColumnGenerator from "components/Common/DataGridColumnGenerator";
import { customerCommandsColumn, customerNameColumn } from "./CustomerColumns";
import { useNavigate } from "react-router-dom";
import { IWebCustomerSummary } from "models/IWebCustomer";
import { useTranslations } from "@fenetech/translations";
import useUserInfo from "helpers/context/User/useUserInfo";
import { AlertColor,  useTheme } from "@mui/material";
import { RoleEnum } from "helpers/enums";
import useIsMobile from "helpers/hooks/useIsMobile";
import useSelectionValuesActions from "helpers/context/SelectionValues/useSelectionValuesActions";
import CustomerAPI from "./CustomerAPI";
import useWait from "helpers/context/Page/useWait";
import CustomersGridMobile from "./CustomersGridMobile";
import CustomDataGridPro from "components/Common/CustomDataGridPro";

interface IProps {
    getCustomerSummaryList: () => void,
    setAlert: (alert: [text: string, alertType: AlertColor] | null) => void,
    filteredCustomerList: IWebCustomerSummary[] | null,
    isMfgSiteVisible: boolean
}

const CustomersGrid: React.FC<IProps> = ({getCustomerSummaryList, setAlert, filteredCustomerList, isMfgSiteVisible}: IProps) => {

    const navigate = useNavigate();
    const tm = useTranslations();
    const user = useUserInfo();
    const theme = useTheme();
    const isMobile = useIsMobile();
    const wait = useWait();
    const selectionValuesActions = useSelectionValuesActions();

    const [isPosting, setIsPosting] = React.useState<boolean>(false);

    React.useEffect(() => {
        wait.Show(isPosting);
    }, [isPosting, wait])

    const clearCache = useCallback(() => {
        selectionValuesActions.ClearCustomersCache();
    }, [selectionValuesActions]);

    const viewCustomerHandler = useCallback((customerID: string) => {
        const url = `view/${encodeURIComponent(customerID)}`;
        navigate(url);
    }, [navigate]);

    const editCustomerHandler = useCallback((customerID: string) => {
        const url = `edit/${encodeURIComponent(customerID)}`;
        navigate(url);
    }, [navigate]);

    const deleteCustomerHandler = useCallback(async (customerID: string) => {
        setIsPosting(true);
            try {
                let result = user.HasRole(RoleEnum.Contractor) ?
                    await CustomerAPI.DeleteWebContractorCustomer(customerID, user.parentCustomerID) :
                    await CustomerAPI.DeleteWebCustomer(customerID);
                if (result.message) {
                    const translated = tm.Get(result);
                    setAlert([translated, "error"]);
                } else {
                    clearCache();
                    getCustomerSummaryList();
                    setAlert([tm.Get("Deleted"), "success"]);
                }
            } finally {
                setIsPosting(false);
            }
    }, [user, tm, getCustomerSummaryList, setAlert, clearCache]);

    const columns = useMemo(() => {
        const generator = new DataGridColumnGenerator(tm, filteredCustomerList, theme, isMobile);
        generator.AddColumn(customerNameColumn("name", "Name", isMfgSiteVisible, tm, isMobile, theme));
        //Only include site ID when Dealers have multiSite
        if (isMfgSiteVisible) {
            generator.TryAddTextColumn("parentSiteName", "Mfg Site", {});
        }
        generator.TryAddTextColumn("shipAddress1", "Address", {});
        generator.TryAddTextColumn("shipCity", "City", {});
        generator.TryAddTextColumn("shipState", "State", {});
        generator.TryAddTextColumn("shipZipCode", "Zip Code", {});
        generator.TryAddTextColumn("salesPersonName", "Salesperson", {});
        generator.AddColumn(customerCommandsColumn(tm, editCustomerHandler, deleteCustomerHandler));

        return generator.GetColumns();
    }, [tm, filteredCustomerList, isMfgSiteVisible, theme, isMobile, editCustomerHandler, deleteCustomerHandler]);
        
        const getRowId: GridRowIdGetter = (row: GridRowModel) => row["customerID"] + row["siteID"];
        
    if (isMobile) {
        return <CustomersGridMobile
            filteredCustomerList={filteredCustomerList}
            isMfgSiteVisible={isMfgSiteVisible}
            viewCustomerHandler={viewCustomerHandler}
            editCustomerHandler={editCustomerHandler}
            deleteCustomerHandler={deleteCustomerHandler}
        />
    } else {
        return <CustomDataGridPro
            getRowId={getRowId}
            columns={columns}
            rows={filteredCustomerList}
            tableName="Customer"
            tableSubName={isMobile ? "Mobile" : ""}
            hideInMobileColumns={[
                "parentSiteName",
                "billAddress1",
                "billCity",
                "billState",
                "billZipCode",
                "salesPersonName",
            ]}
        />
    }
}

export default CustomersGrid;